import imagesLoaded from "imagesloaded";
import _ from "lodash";

export class ContentResizer {
  constructor(args = {}) {
    if (typeof args.target === "undefined") {
      throw new Error(
        "Missing `target` key. You need to pass an object to `ContentResizer()` where you pass the DOM element as `target`."
      );
    }
    if (typeof args.target.hasAttribute !== "function") {
      throw new Error("`target` is not a DOM element.");
    }
    if (
      typeof args.resizeMethod === "undefined" &&
      !args.target.hasAttribute("data-content-resize")
    ) {
      throw new Error(
        "No resize method has been set. DOM element must either have a `data-content-resize` attribute or the instance must be initiate with {resizeMethod: 'some-method'}."
      );
    }
    this.container = args.target;
    this.resizeMethod = args.resizeMethod
      ? args.resizeMethod
      : this.container.getAttribute("data-content-resize");
    this.validResizeMethods = ["contain", "cover"];
    if (!_.includes(this.validResizeMethods, this.resizeMethod)) {
      let message = `"${this.resizeMethod}" is not a valid resize method.`;
      message += `Please use one of the following: ${this.validResizeMethods}. `;
      message += `The content will be formatted using "cover".`;
      console.warn(message);
    }
    this.sizeReductionPercentage = args.sizeReductionPercentage
      ? args.sizeReductionPercentage
      : 0;
    this.autoResizeDelay = args.autoResizeDelay
      ? args.autoResizeDelay
      : 100;
    this.resizableContainer =
      typeof args.resizableContainer !== "undefined"
        ? args.resizableContainer
        : true;
    this.init();
    this.setEvents(args);
  }

  init() {
    this.resizeContainer();
    this.resizeContent();
  }

  setEvents(args) {
    if (args.autoResize !== false) {
      // WARNING please make sure debounce time is higher than the one affecting the container.
      //   Because there is no way to know the order in which the `resize` callbacks are run,
      //   the only way to ensure the content is resized AFTER the container is to wait for
      //   a longer period of time.
      window.addEventListener(
        "resize",
        _.debounce(this.resize.bind(this), this.autoResizeDelay)
      );
    }
  }

  resize() {
    this.resizeContainer();
    this.resizeContent();
  }

  resizeContainer() {
    if (this.resizableContainer) {
      this.container.style.height = `${window.innerHeight}px`;
    }
    // Store container size to avoid performance drawbacks
    this.containerSize = {
      width: this.container.offsetWidth,
      height: this.container.offsetHeight,
    };

    this.containerSize.ratio =
      this.containerSize.width / this.containerSize.height;
  }

  resizeContent() {
    this.resizeImages();
    this.resizeVideos();
  }

  resizeImages() {
    imagesLoaded(this.container, () => {
      // Get all the descendants images
      const images = this.container.querySelectorAll("img");
      for(let i = 0; i < images.length; i++) {
        this.resizeSpecificImage();
      }
    });
  }

  resizeSpecificImage(image) {
    const size = {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
    size.ratio = size.width / size.height;
    if (this.resizeMethod === "contain") {
      this.resizeContain(size, image);
    } else {
      this.resizeCover(size, image);
    }
  }

  resizeContain(size, element) {
    // Calculate the contained size (which is relatively smaller than the actual container size)
    // NOTE: the border must be the same size on both top/bottom and left/right. Therefore
    //   it's calculated based on a % of the container width (percentage might change based
    //   on sizeReductionPercentage). Be aware that you might not see this detail as the images
    //   aren't cropped so they might not get the same proportion of the containedSize area.
    const reduction = this.containerSize.width * this.sizeReductionPercentage;
    const containedSize = {
      width: this.containerSize.width - reduction,
      height: this.containerSize.height - reduction,
    };
    containedSize.ratio = containedSize.width / containedSize.height;
    if (containedSize.ratio > size.ratio) {
      const newWidth = size.ratio * containedSize.height;
      element.style.width = `${newWidth}px`;
      element.style.height = `${containedSize.height}px`;
    } else {
      const newHeight = containedSize.width / size.ratio;
      element.style.width = `${containedSize.width}px`;
      element.style.height = `${newHeight}px`;
    }
  }

  resizeCover(size, element) {
    if (this.containerSize.ratio > size.ratio) {
      const newHeight = this.containerSize.width / size.ratio;
      element.style.width = `${this.containerSize.width}px`;
      element.style.height = `${newHeight}px`;
    } else {
      const newWidth = size.ratio * this.containerSize.height;
      element.style.width = `${newWidth}px`;
      element.style.height = `${this.containerSize.height}px`;
    }
  }

  resizeVideos() {
    const videos = this.container.querySelectorAll("video");
    for(let i = 0; i < videos.length; i++) {
      const video = videos[i];
      this.resizeVideo(video);
    }
  }

  resizeVideo(video) {
    const size = {};
    // If video is not ready:
    // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement/videoWidth
    // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
    if (video.readyState === 0) {
      // Temporarily resizing as a 16:9
      size.width = 16;
      size.height = 9;
      // When it will be available resize it properly
      video.addEventListener("canplaythrough", () => {
        this.resizeVideo(video);
      });
    } else {
      size.width = video.videoWidth;
      size.height = video.videoHeight;
    }
    size.ratio = size.width / size.height;
    if (this.resizeMethod === "contain") {
      this.resizeContain(size, video);
    } else {
      this.resizeCover(size, video);
    }
  }
}



// WEBPACK FOOTER //
// ./scripts/util/ContentResizer.js